// ============================================================
// TERROR TCG — COMPANION SYSTEM
// Registry + helpers for Plaza companions.
//
// Design principles:
//  - One source of truth per companion (this file).
//  - Schema is holo-ready from day one: every companion has a
//    `variant` field ("standard" | "gold" | "mythic" | etc.) and
//    an `effect` field for future cosmetic overlays.
//  - Ownership is derived from three sources (in priority order):
//      1. Trophy unlocks    — profile.companions[id]
//      2. Collection-backed — profile.collection[sourceCard]
//      3. Starter           — profile.starterDeckId
//  - Sol Convert cost = tier cost. Paid in Crests at swap time.
//  - Adding a companion = one entry in COMPANIONS. No code changes.
// ============================================================

// ── TIERS — match the TCG Escendant ascension hierarchy ─────
export const TIERS = {
  ESCLING:  "escling",
  BEB:      "beb",
  CHAMPION: "champion",
  LORD:     "lord",
  MYTHIC:   "mythic",   // reserved for J'ynxi-tier trophies
};

// ── Sol Converter cost by tier (Crests per swap) ────────────
export const SOL_CONVERTER_COSTS = {
  escling:  5,
  beb:      10,
  champion: 15,
  lord:     20,
  mythic:   30,
};

// ── Ilk palette — drives placeholder colors ─────────────────
export const ILK_COLORS = {
  Space:   { primary: "#22d3ee", glow: "#0e7490", deep: "#083344" },
  Phantom: { primary: "#c084fc", glow: "#7c3aed", deep: "#2e1065" },
  Dragon:  { primary: "#f87171", glow: "#dc2626", deep: "#450a0a" },
  Time:    { primary: "#fbbf24", glow: "#d97706", deep: "#451a03" },
  Neutral: { primary: "#e8d5a3", glow: "#c8963c", deep: "#3d2e14" },
};

// ── Variant styling — holo-ready from day one ───────────────
// When real art is dropped in, art path can be per-variant.
export const VARIANTS = {
  standard: { label: "Standard",    shine: false, aura: null },
  gold:     { label: "Gold Foil",   shine: true,  aura: "#f0d060" },
  holo:     { label: "Holographic", shine: true,  aura: "rainbow" },
  mythic:   { label: "Mythic",      shine: true,  aura: "#c084fc" },
};

// ─────────────────────────────────────────────────────────────
// COMPANION REGISTRY
// ─────────────────────────────────────────────────────────────
// Schema:
//   id            — unique, matches source card id when collection-backed
//   name          — display name
//   tier          — escling | beb | champion | lord | mythic
//   ilk           — Space | Phantom | Dragon | Time | Neutral
//   source        — how this companion becomes unlockable:
//                     "collection:<cardId>"  — own the card
//                     "starter:<deckId>"     — free with starter deck
//                     "story:<opponentId>"   — defeat story opponent
//                     "dungeon:<dungeonId>"  — clear dungeon
//                     "depth:<rankId>"       — reach Depth rank
//                     "secret:<code>"        — hidden unlock
//   artKey        — sprite key in COMPANION_ART (null = use placeholder)
//   description   — short flavor shown in Menagerie
//   variants      — list of available variants (default ["standard"])
// ─────────────────────────────────────────────────────────────

export const COMPANIONS = {

  // ── ESCLINGS (base ilk sprites — 4) ─────────────────────────
  escling_space: {
    id: "escling_space", name: "Space Escling", tier: "escling", ilk: "Space",
    source: "collection:escling_space",
    originLore: "Drifted out of a starfield and refused to leave your side.",
    artKey: null,
    description: "A primordial Space-ilk essence. Flickers with starlight.",
    variants: ["standard"],
  },
  escling_phantom: {
    id: "escling_phantom", name: "Phantom Escling", tier: "escling", ilk: "Phantom",
    source: "collection:escling_phantom",
    originLore: "Seeped through the veil one cold night and stayed for the warmth.",
    artKey: null,
    description: "A wisp of Phantom-ilk vapor. Cold to the touch. Mostly.",
    variants: ["standard"],
  },
  escling_dragon: {
    id: "escling_dragon", name: "Dragon Escling", tier: "escling", ilk: "Dragon",
    source: "collection:escling_dragon",
    originLore: "A stray ember that chose your pocket over the pyre.",
    artKey: null,
    description: "A sparking Dragon-ilk ember. Will bite if provoked.",
    variants: ["standard"],
  },
  escling_time: {
    id: "escling_time", name: "Time Escling", tier: "escling", ilk: "Time",
    source: "collection:escling_time",
    originLore: "Slipped out of a second that never finished passing.",
    artKey: null,
    description: "A ticking Time-ilk fragment. Never late. Never early.",
    variants: ["standard"],
  },

  // ── STARTER BEBS (12 — free with matching starter deck) ────
  h01_beb: {
    id: "h01_beb", name: "H-01 Beb", tier: "beb", ilk: "Space",
    source: "starter:starter_h01",
    originLore: "Hatched under a blue sun the day you first took the field.",
    artKey: null,
    description: "A newborn Blue Giant. Small. Radiant. Full of potential.",
    variants: ["standard"],
  },
  h09_beb: {
    id: "h09_beb", name: "H-09 Beb", tier: "beb", ilk: "Space",
    source: "starter:starter_h09",
    originLore: "Assigned to you by the Star Generals. Refuses to be dismissed.",
    artKey: null,
    description: "A Neutron-seed. Denser than it looks. Much denser.",
    variants: ["standard"],
  },
  xulan_beb: {
    id: "xulan_beb", name: "Xulan Beb", tier: "beb", ilk: "Space",
    source: "starter:starter_xulan",
    originLore: "Followed you out of the Void, plotting someone else's revenge.",
    artKey: null,
    description: "A voidling. Already dreaming of the nightmares it will make.",
    variants: ["standard"],
  },
  spectral_tiger_beb: {
    id: "spectral_tiger_beb", name: "Spectral Tiger Beb", tier: "beb", ilk: "Phantom",
    source: "starter:starter_spectral_tiger",
    originLore: "A spoiled kitten of the Spirit Worlds. You're its favorite toy.",
    artKey: null,
    description: "A translucent cub. Purrs in frequencies you can't quite hear.",
    variants: ["standard"],
  },
  shadow_fox_beb: {
    id: "shadow_fox_beb", name: "Shadow Fox Beb", tier: "beb", ilk: "Phantom",
    source: "starter:starter_shadow_fox",
    originLore: "Padded up from the Bottomless Depths and curled at your feet.",
    artKey: null,
    description: "A kit of living umbra. Slips between the cracks of reality.",
    variants: ["standard"],
  },
  inverted_serpent_beb: {
    id: "inverted_serpent_beb", name: "Inverted Serpent Beb", tier: "beb", ilk: "Phantom",
    source: "starter:starter_inverted_serpent",
    originLore: "Uncoiled from a mirror's wrong side to taste your world.",
    artKey: null,
    description: "A hatchling with fangs that face the wrong way. By design.",
    variants: ["standard"],
  },
  z4z_beb: {
    id: "z4z_beb", name: "Z4Z Beb", tier: "beb", ilk: "Time",
    source: "starter:starter_z4z",
    originLore: "Ticked awake in your hands. Has counted every second since.",
    artKey: null,
    description: "A clockwork initiate. All ticks, no tocks — yet.",
    variants: ["standard"],
  },
  dg0_beb: {
    id: "dg0_beb", name: "DG-0 Beb", tier: "beb", ilk: "Time",
    source: "starter:starter_dg0",
    originLore: "Booted from a forgotten epoch with your name in its registry.",
    artKey: null,
    description: "A proto-judge. Tiny gavel. Unshakable conviction.",
    variants: ["standard"],
  },
  antdroid_beb: {
    id: "antdroid_beb", name: "Antdroid Beb", tier: "beb", ilk: "Time",
    source: "starter:starter_antdroid",
    originLore: "Built itself from spare seconds to march beside you.",
    artKey: null,
    description: "A single antdroid, separated from the swarm. For now.",
    variants: ["standard"],
  },
  lucky_dragon_beb: {
    id: "lucky_dragon_beb", name: "Lucky Dragon Beb", tier: "beb", ilk: "Dragon",
    source: "starter:starter_lucky_dragon",
    originLore: "Tumbled out of a fortune no one else was owed.",
    artKey: null,
    description: "A coin-scaled whelp. Flip it before any big decision.",
    variants: ["standard"],
  },
  premier_dragon_beb: {
    id: "premier_dragon_beb", name: "Premier Dragon Beb", tier: "beb", ilk: "Dragon",
    source: "starter:starter_premier_dragon",
    originLore: "First of its clutch — it decided you were first of yours.",
    artKey: null,
    description: "A hatchling with charisma. Already knows your name.",
    variants: ["standard"],
  },
  chaos_dragon_beb: {
    id: "chaos_dragon_beb", name: "Chaos Dragon Beb", tier: "beb", ilk: "Dragon",
    source: "starter:starter_chaos_dragon",
    originLore: "Hatched sideways through probability and liked what it found.",
    artKey: null,
    description: "A newborn storm. Dangerous at any size. Especially small.",
    variants: ["standard"],
  },

  // ── CHAMPIONS (12 — unlock via collection) ─────────────────
  h01_champion: {
    id: "h01_champion", name: "H-01 Champion", tier: "champion", ilk: "Space",
    source: "collection:h01_champion",
    originLore: "Answered the Blue Giant's call the moment its card was yours.",
    artKey: null,
    description: "The Blue Giant, ascendant. Gravity bends. So do adversaries.",
    variants: ["standard"],
  },
  h09_champion: {
    id: "h09_champion", name: "H-09 Champion", tier: "champion", ilk: "Space",
    source: "collection:h09_champion",
    originLore: "Swore the Neutron oath when its likeness joined your Grimoire.",
    artKey: null,
    description: "The Neutron Star, focused. One step. Planet-cracking.",
    variants: ["standard"],
  },
  xulan_champion: {
    id: "xulan_champion", name: "Xulan Champion", tier: "champion", ilk: "Space",
    source: "collection:xulan_champion",
    originLore: "Bound its vendetta to yours the day you claimed its card.",
    artKey: null,
    description: "The void-walker, awake. It remembers every dream you've had.",
    variants: ["standard"],
  },
  spectral_tiger_champion: {
    id: "spectral_tiger_champion", name: "Spectral Tiger Champion", tier: "champion", ilk: "Phantom",
    source: "collection:spectral_tiger_champion",
    originLore: "Prowled out of its portrait once you'd earned the right.",
    artKey: null,
    description: "Half here, half hunting. You will not see the other half.",
    variants: ["standard"],
  },
  shadow_fox_champion: {
    id: "shadow_fox_champion", name: "Shadow Fox Champion", tier: "champion", ilk: "Phantom",
    source: "collection:shadow_fox_champion",
    originLore: "Its card cast a shadow — this stepped out of it.",
    artKey: null,
    description: "The vulpine silence. It has been beside you the whole time.",
    variants: ["standard"],
  },
  inverted_serpent_champion: {
    id: "inverted_serpent_champion", name: "Inverted Serpent Champion", tier: "champion", ilk: "Phantom",
    source: "collection:inverted_serpent_champion",
    originLore: "Read its own card backwards and found your name.",
    artKey: null,
    description: "It strikes the way you didn't expect. Then the other way.",
    variants: ["standard"],
  },
  z4z_champion: {
    id: "z4z_champion", name: "Z4Z Champion", tier: "champion", ilk: "Time",
    source: "collection:z4z_champion",
    originLore: "Synchronized to you when its card entered your timeline.",
    artKey: null,
    description: "The clockwork, fully wound. Every gear where it should be.",
    variants: ["standard"],
  },
  dg0_champion: {
    id: "dg0_champion", name: "DG-0 Champion", tier: "champion", ilk: "Time",
    source: "collection:dg0_champion",
    originLore: "Decrypted your ownership records and enlisted on the spot.",
    artKey: null,
    description: "Judgment in motion. You have been read your rights.",
    variants: ["standard"],
  },
  antdroid_champion: {
    id: "antdroid_champion", name: "Antdroid Champion", tier: "champion", ilk: "Time",
    source: "collection:antdroid_champion",
    originLore: "Assembled itself rank by rank as its card settled in your keeping.",
    artKey: null,
    description: "A pillar of the colony. Lifts forty times its weight in law.",
    variants: ["standard"],
  },
  lucky_dragon_champion: {
    id: "lucky_dragon_champion", name: "Lucky Dragon Champion", tier: "champion", ilk: "Dragon",
    source: "collection:lucky_dragon_champion",
    originLore: "Its card came with interest — this is the interest.",
    artKey: null,
    description: "The odds bend around it. Gently. Then sharply.",
    variants: ["standard"],
  },
  premier_dragon_champion: {
    id: "premier_dragon_champion", name: "Premier Dragon Champion", tier: "champion", ilk: "Dragon",
    source: "collection:premier_dragon_champion",
    originLore: "Stepped down from the card's frame to inspect its new keeper.",
    artKey: null,
    description: "Grace with scales. Every entrance is the entrance.",
    variants: ["standard"],
  },
  chaos_dragon_champion: {
    id: "chaos_dragon_champion", name: "Chaos Dragon Champion", tier: "champion", ilk: "Dragon",
    source: "collection:chaos_dragon_champion",
    originLore: "Escaped its own card art. The card doesn't seem to mind.",
    artKey: null,
    description: "The storm, maturing. Still chooses chaos. Always.",
    variants: ["standard"],
  },

  // ── LORDS (12 — unlock via collection, apex tier) ──────────
  h01_lord: {
    id: "h01_lord", name: "H-01 Lord", tier: "lord", ilk: "Space",
    source: "collection:h01_lord",
    originLore: "A Blue Giant crowned — it keeps company with those who hold its writ.",
    artKey: null,
    description: "Super Nova made flesh. Walking next to you is a privilege.",
    variants: ["standard"],
  },
  h09_lord: {
    id: "h09_lord", name: "H-09 Lord", tier: "lord", ilk: "Space",
    source: "collection:h09_lord",
    originLore: "The Neutron court follows wherever its sovereign's card is kept.",
    artKey: null,
    description: "Radiant Shower incarnate. It chose you. Do not forget.",
    variants: ["standard"],
  },
  xulan_lord: {
    id: "xulan_lord", name: "Xulan Lord", tier: "lord", ilk: "Space",
    source: "collection:xulan_lord",
    originLore: "Its revenge complete, it wanders with whoever holds its legend.",
    artKey: null,
    description: "Sweet Dreams, walking. The ones you wake up screaming from.",
    variants: ["standard"],
  },
  spectral_tiger_lord: {
    id: "spectral_tiger_lord", name: "Spectral Tiger Lord", tier: "lord", ilk: "Phantom",
    source: "collection:spectral_tiger_lord",
    originLore: "An apex of the Spirit Worlds, leashed only by its own card.",
    artKey: null,
    description: "Haunting Presence. Felt three rooms away. Closer, now.",
    variants: ["standard"],
  },
  shadow_fox_lord: {
    id: "shadow_fox_lord", name: "Shadow Fox Lord", tier: "lord", ilk: "Phantom",
    source: "collection:shadow_fox_lord",
    originLore: "The darkest sphere gave it up when its card came to you.",
    artKey: null,
    description: "Royal Darkness. Every shadow in the Plaza bows a little.",
    variants: ["standard"],
  },
  inverted_serpent_lord: {
    id: "inverted_serpent_lord", name: "Inverted Serpent Lord", tier: "lord", ilk: "Phantom",
    source: "collection:inverted_serpent_lord",
    originLore: "Rules the mirror-side; visits yours while you keep its card.",
    artKey: null,
    description: "Inverted Fangs. You have been bitten. You didn't notice.",
    variants: ["standard"],
  },
  z4z_lord: {
    id: "z4z_lord", name: "Z4Z Lord", tier: "lord", ilk: "Time",
    source: "collection:z4z_lord",
    originLore: "A sovereign of seconds, sworn to the hand that holds its hour.",
    artKey: null,
    description: "Apocalypse Now. The countdown started with you.",
    variants: ["standard"],
  },
  dg0_lord: {
    id: "dg0_lord", name: "DG-0 Lord", tier: "lord", ilk: "Time",
    source: "collection:dg0_lord",
    originLore: "An epoch's last archive, backed up to your collection.",
    artKey: null,
    description: "Justice Reigns. Verdict already delivered. You just haven't heard it.",
    variants: ["standard"],
  },
  antdroid_lord: {
    id: "antdroid_lord", name: "Antdroid Lord", tier: "lord", ilk: "Time",
    source: "collection:antdroid_lord",
    originLore: "A legion condensed to one — quartered wherever its card lives.",
    artKey: null,
    description: "King of the Hill. The hill is wherever it is standing.",
    variants: ["standard"],
  },
  lucky_dragon_lord: {
    id: "lucky_dragon_lord", name: "Lucky Dragon Lord", tier: "lord", ilk: "Dragon",
    source: "collection:lucky_dragon_lord",
    originLore: "Fortune's landlord. It moved in the day its card did.",
    artKey: null,
    description: "Dragon Eyes. Rolls sevens. Rolls them again. And again.",
    variants: ["standard"],
  },
  premier_dragon_lord: {
    id: "premier_dragon_lord", name: "Premier Dragon Lord", tier: "lord", ilk: "Dragon",
    source: "collection:premier_dragon_lord",
    originLore: "Eldest of the clutch, roosting on the card that bears its name.",
    artKey: null,
    description: "Show Stomper. The Plaza is the stage. You are the opener.",
    variants: ["standard"],
  },
  chaos_dragon_lord: {
    id: "chaos_dragon_lord", name: "Chaos Dragon Lord", tier: "lord", ilk: "Dragon",
    source: "collection:chaos_dragon_lord",
    originLore: "Entropy with a crown. Its card is the only leash it tolerates.",
    artKey: null,
    description: "I or None. Be careful. This one has beaten players who thought they owned it.",
    variants: ["standard"],
  },

  // ── STORY TROPHIES (earned, not collectable) ───────────────
  suhnker_trophy: {
    id: "suhnker_trophy", name: "S'uhnker", tier: "champion", ilk: "Space",
    source: "story:suhnker",
    originLore: "Yielded its banner the day you broke it in battle.",
    artKey: null, isTrophy: true,
    description: "The Sentinel of the Outer Gate, kept close. It still does not speak.",
    variants: ["standard"],
  },
  zhefir_trophy: {
    id: "zhefir_trophy", name: "Z'hefir", tier: "champion", ilk: "Phantom",
    source: "story:zhefir",
    originLore: "Haunts its conqueror — that's you — out of grudging respect.",
    artKey: null, isTrophy: true,
    description: "The Keeper of Shadows, tethered. Watches the spaces between stones.",
    variants: ["standard"],
  },
  tyrent_trophy: {
    id: "tyrent_trophy", name: "T'yrent", tier: "lord", ilk: "Dragon",
    source: "story:tyrent",
    originLore: "Bows to exactly one being: the one who brought it down.",
    artKey: null, isTrophy: true,
    description: "The Lord you dismantled. Walks beside you now, for reasons it will not share.",
    variants: ["standard"],
  },

  // ── MYTHIC (reserved — beat J'ynxi to unlock) ──────────────
  jynxi: {
    id: "jynxi", name: "J'ynxi", tier: "mythic", ilk: "Phantom",
    source: "story:jynxi",
    originLore: "Friend or faux? It grinned, shook your hand, and never let go.",
    artKey: "jynxi", isTrophy: true, // current default art is jynxi.webp
    description: "The Nefarious herself. Somehow, she chose you.",
    variants: ["standard", "mythic"],
  },

};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

/** Get a companion by id, null-safe. */
export function getCompanion(id) {
  return COMPANIONS[id] || null;
}

/** Sol Converter cost for swapping TO this companion. */
export function getSolConverterCost(companionId) {
  const c = getCompanion(companionId);
  if (!c) return 0;
  return SOL_CONVERTER_COSTS[c.tier] || 0;
}

/** Starter deck id → starter companion id (the Beb mascot). */
export function getStarterCompanionId(starterDeckId) {
  if (!starterDeckId) return "jynxi"; // legacy fallback
  const mapping = {
    starter_h01:              "h01_beb",
    starter_h09:              "h09_beb",
    starter_xulan:            "xulan_beb",
    starter_spectral_tiger:   "spectral_tiger_beb",
    starter_shadow_fox:       "shadow_fox_beb",
    starter_inverted_serpent: "inverted_serpent_beb",
    starter_z4z:              "z4z_beb",
    starter_dg0:              "dg0_beb",
    starter_antdroid:         "antdroid_beb",
    starter_lucky_dragon:     "lucky_dragon_beb",
    starter_premier_dragon:   "premier_dragon_beb",
    starter_chaos_dragon:     "chaos_dragon_beb",
  };
  return mapping[starterDeckId] || "jynxi";
}

/**
 * Determine if a companion is unlocked for a given profile.
 * Priority:
 *   1. Explicit unlocks on profile.companions[id] (trophies, etc.)
 *   2. Starter match
 *   3. Collection match (owning the card)
 */
export function isCompanionUnlocked(profile, companionId) {
  if (!profile || !companionId) return false;
  const c = getCompanion(companionId);
  if (!c) return false;

  // 1. Explicit unlocks (trophies, event rewards, secrets)
  if (profile.companions && profile.companions[companionId]) return true;

  // 2. Source-based unlocks
  if (!c.source) return false;
  const [type, key] = c.source.split(":");

  if (type === "starter") {
    return profile.starterDeckId === key;
  }
  if (type === "collection") {
    const col = profile.collection || {};
    const entry = col[key];
    // collection entries are either truthy counts or { count, holo } objects
    if (typeof entry === "number") return entry > 0;
    if (entry && typeof entry === "object") return (entry.count || 0) > 0;
    return !!entry;
  }
  if (type === "story" || type === "dungeon" || type === "depth" || type === "secret") {
    // These require explicit profile.companions[id] entry — covered in step 1.
    return false;
  }
  return false;
}

/** List all companions this profile currently has unlocked. */
export function getUnlockedCompanions(profile) {
  if (!profile) return [];
  return Object.keys(COMPANIONS).filter(id => isCompanionUnlocked(profile, id));
}

/** Unlock hint text for a locked companion (shown in Menagerie). */
export function getUnlockHint(companionId) {
  const c = getCompanion(companionId);
  if (!c || !c.source) return "???";
  const [type, key] = c.source.split(":");
  if (type === "starter")     return "Chosen as starter deck.";
  if (type === "collection")  return `Own the ${c.name} card.`;
  if (type === "story")       return `Defeat ${key} in Story Mode.`;
  if (type === "dungeon")     return `Clear the ${key} dungeon.`;
  if (type === "depth")       return `Reach ${key} rank in Endless Depths.`;
  if (type === "secret")      return "???";
  return "???";
}

/** Group companions by tier for UI display. */
export function groupCompanionsByTier() {
  const groups = { escling: [], beb: [], champion: [], lord: [], mythic: [] };
  for (const c of Object.values(COMPANIONS)) {
    if (groups[c.tier]) groups[c.tier].push(c);
  }
  return groups;
}

// ─────────────────────────────────────────────────────────────
// RADIANT TIER SYSTEM
// ─────────────────────────────────────────────────────────────
//
// RADIANT_VERSIONS is an ARRAY of (companion, tier) offerings.
// Each entry: { companionId, tier } where tier is a holo_* key
// that indexes into HOLO_TIER_INFO (PackArt.js).
//
// 16 curated pairings across all 9 tiers. Companions are matched
// thematically to their tier where it makes sense (J'ynxi → jynxi,
// dragons → dragon-flavored tiers, etc.).
// ─────────────────────────────────────────────────────────────

export const RADIANT_TIER_COST = {
  holo_silver:    8,
  holo_gold:     11,
  holo_void:     13,
  holo_shadow:   13,
  holo_jynxi:    14,
  holo_depths:   15,
  holo_solrend:  16,
  holo_aeternus: 18,
  holo_mythic:   22,
};

export const RADIANT_VERSIONS = [
  // ── Silver (entry tier) ──
  { companionId: "jynxi",                 tier: "holo_silver"   },
  { companionId: "spectral_tiger_lord",   tier: "holo_silver"   },
  // ── Gold ──
  { companionId: "lucky_dragon_lord",     tier: "holo_gold"     },
  { companionId: "premier_dragon_lord",   tier: "holo_gold"     },
  // ── Void ──
  { companionId: "z4z_lord",              tier: "holo_void"     },
  { companionId: "dg0_lord",              tier: "holo_void"     },
  // ── Shadow ──
  { companionId: "shadow_fox_lord",       tier: "holo_shadow"   },
  { companionId: "inverted_serpent_lord", tier: "holo_shadow"   },
  // ── J'ynxi green ──
  { companionId: "jynxi",                 tier: "holo_jynxi"    },
  { companionId: "chaos_dragon_lord",     tier: "holo_jynxi"    },
  // ── Depths ──
  { companionId: "xulan_lord",            tier: "holo_depths"   },
  { companionId: "antdroid_lord",         tier: "holo_depths"   },
  // ── Sol Rend ──
  { companionId: "h01_lord",              tier: "holo_solrend"  },
  { companionId: "h09_lord",              tier: "holo_solrend"  },
  // ── Aeternus ──
  { companionId: "spectral_tiger_lord",   tier: "holo_aeternus" },
  // ── Mythic (apex) ──
  { companionId: "jynxi",                 tier: "holo_mythic"   },
];

const TIER_SHORT = {
  holo_silver: "sil", holo_gold: "gld", holo_mythic: "myth", holo_void: "void",
  holo_solrend: "sol", holo_aeternus: "aet", holo_shadow: "shd", holo_depths: "dep", holo_jynxi: "jyn",
};

/** Deterministic id for a (companion, tier) radiant pairing. */
export function radiantId(companionId, tier) {
  return `rad_${companionId}_${TIER_SHORT[tier] || tier}`;
}

/** Storage key for unlockedRadiants. */
export function radiantKey(companionId, tier) {
  return `${companionId}:${tier}`;
}

/** Cost for a radiant tier. */
export function getRadiantCost(tier) {
  return RADIANT_TIER_COST[tier] || 12;
}

/** Has the player unlocked this specific radiant pairing? */
export function isRadiantUnlocked(profile, companionId, tier) {
  if (!profile) return false;
  const owned = profile.unlockedRadiants || {};
  return !!owned[radiantKey(companionId, tier)];
}

// ─────────────────────────────────────────────────────────────
// PROVENANCE — sigil id + origin lore for the companion bubble
// ─────────────────────────────────────────────────────────────
// Canon lore law: a companion's origin line is NEVER literal
// ("from a starter deck") — each companion carries its own
// flavorful originLore. Collection-bonded companions inherit
// their source card's actual top provenance mark.
export function getCompanionProvenance(companionId, profile = null) {
  const c = getCompanion(companionId);
  if (!c) return { sigil: null, lore: "???" };
  const lore = c.originLore || getUnlockHint(companionId);
  const [type, key] = (c.source || "").split(":");
  let sigil = null;
  if (type === "starter") sigil = "starter";
  else if (type === "story" || type === "dungeon" || type === "depth") sigil = "earned";
  else if (type === "collection") {
    const rec = profile?.collection?.[key];
    const counts = {};
    if (rec?.sources) for (const [s, n] of Object.entries(rec.sources)) counts[s] = (counts[s] || 0) + (n || 0);
    if (rec?.sourceTiers) for (const m of Object.values(rec.sourceTiers)) for (const [s, n] of Object.entries(m || {})) counts[s] = (counts[s] || 0) + (n || 0);
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    sigil = top ? top[0] : null;
  }
  return { sigil, lore };
}
